import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Tag,
  message,
  Popconfirm,
  Typography,
  Row,
  Col,
  Statistic,
  Tooltip,
  Checkbox,
} from 'antd';
import {
  MailOutlined,
  EyeOutlined,
  DeleteOutlined,
  ReloadOutlined,
  SearchOutlined,
  PlusOutlined,
  SendOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { formatSimpleDateTime, formatLocalizedDateTime } from '../../utils/dateUtils';
import { emailLogService, EmailLog, EmailStats, SearchEmailLogsDto, ResendEmailDto } from '../../services/emailLogService';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;



const EmailLogManagement: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [emailLogs, setEmailLogs] = useState<EmailLog[]>([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [searchForm] = Form.useForm();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [resendModalVisible, setResendModalVisible] = useState(false);
  const [selectedEmailLog, setSelectedEmailLog] = useState<EmailLog | null>(null);
  const [resendForm] = Form.useForm();
  const [hideOriginalAfterResend, setHideOriginalAfterResend] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // 이메일 로그 목록 조회
  const fetchEmailLogs = async (params?: SearchEmailLogsDto) => {
    setLoading(true);
    try {
      const data = await emailLogService.getEmailLogs(params);
      setEmailLogs(data.logs);
      setTotal(data.total);
      setPagination(prev => ({ ...prev, total: data.total }));
    } catch (error: any) {
      message.error('이메일 로그 조회에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  // 통계 조회
  const fetchStats = async () => {
    try {
      const data = await emailLogService.getEmailStats();
      setStats(data);
    } catch (error: any) {
      message.error('통계 조회에 실패했습니다.');
    }
  };

  // 이메일 로그 삭제
  const handleDelete = async (emailLogId: string) => {
    try {
      await emailLogService.deleteEmailLog(emailLogId);
      message.success('이메일 로그가 삭제되었습니다.');
      fetchEmailLogs();
    } catch (error: any) {
      message.error('이메일 로그 삭제에 실패했습니다.');
    }
  };

  // 이메일 재발송
  const handleResend = async (values: ResendEmailDto) => {
    if (!selectedEmailLog) return;

    try {
      await emailLogService.resendEmail(selectedEmailLog.email_log_id, values);
      message.success('이메일이 재발송되었습니다.');
      setResendModalVisible(false);
      resendForm.resetFields();
      
      // 재발송 후 원본 메일 숨기기 옵션이 활성화된 경우
      if (hideOriginalAfterResend) {
        // 현재 검색 조건에 원본 메일 제외 필터 추가
        const currentParams = searchForm.getFieldsValue();
        searchForm.setFieldsValue({
          ...currentParams,
          is_resend: 'false', // 원본 메일만 표시
        });
      }
      
      fetchEmailLogs();
    } catch (error: any) {
      message.error('이메일 재발송에 실패했습니다.');
    }
  };

  // 검색
  const handleSearch = (values: any) => {
    const params: SearchEmailLogsDto = {
      ...values,
      limit: pagination.pageSize,
      offset: 0,
    };
    fetchEmailLogs(params);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 페이지 변경
  const handleTableChange = (pagination: any) => {
    const params = searchForm.getFieldsValue();
    const newParams = {
      ...params,
      limit: pagination.pageSize,
      offset: (pagination.current - 1) * pagination.pageSize,
    };
    fetchEmailLogs(newParams);
    setPagination(prev => ({ ...prev, current: pagination.current }));
  };

  // 상태별 색상
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SENT': return 'green';
      case 'FAILED': return 'red';
      case 'PENDING': return 'orange';
      case 'CANCELLED': return 'gray';
      default: return 'default';
    }
  };

  // 타입별 색상
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'BULK_EMAIL': return 'blue';
      case 'BUG_REPORT_RESOLUTION': return 'purple';
      case 'SYSTEM_NOTIFICATION': return 'cyan';
      default: return 'default';
    }
  };

  // 컬럼 정의
  const columns = [
    {
      title: '제목',
      dataIndex: 'subject',
      key: 'subject',
      width: 250,
      ellipsis: false,
      render: (text: string, record: EmailLog) => (
        <div style={{ 
          wordBreak: 'break-word', 
          whiteSpace: 'normal',
          maxWidth: '250px',
          overflow: 'hidden'
        }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Text style={{ 
              wordBreak: 'break-word', 
              whiteSpace: 'normal',
              maxWidth: '250px',
              overflow: 'hidden'
            }}>
              {text}
            </Text>
            {record.metadata?.is_resend && (
              <Tag color="orange">재발송</Tag>
            )}
          </Space>
        </div>
      ),
    },
    {
      title: '타입',
      dataIndex: 'email_type',
      key: 'email_type',
      width: 80,
      render: (type: string) => (
        <Tag color={getTypeColor(type)}>
          {type === 'BULK_EMAIL' ? '대량' :
           type === 'BUG_REPORT_RESOLUTION' ? '버그' :
           type === 'SYSTEM_NOTIFICATION' ? '시스템' : type}
        </Tag>
      ),
    },
    {
      title: '상태',
      dataIndex: 'status',
      key: 'status',
      width: 70,
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status === 'SENT' ? '발송' :
           status === 'FAILED' ? '실패' :
           status === 'PENDING' ? '대기' :
           status === 'CANCELLED' ? '취소' : status}
        </Tag>
      ),
    },
    {
      title: '수신자',
      dataIndex: 'recipients',
      key: 'recipients',
      width: 60,
      render: (recipients: string[]) => (
        <Text>{recipients.length}명</Text>
      ),
    },
    {
      title: '발송자',
      dataIndex: 'sent_by',
      key: 'sent_by',
      width: 80,
      render: (sentBy: any) => (
        <Text ellipsis title={sentBy?.username || '-'}>
          {sentBy?.username || '-'}
        </Text>
      ),
    },
    {
      title: '생성일',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 120,
      render: (date: string) => formatSimpleDateTime(date),
    },
    {
      title: '작업',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (record: EmailLog) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedEmailLog(record);
              setDetailModalVisible(true);
            }}
            title="상세보기"
          />
          <Button
            type="primary"
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => {
              setSelectedEmailLog(record);
              resendForm.setFieldsValue({
                subject: record.subject,
                content: record.content,
                recipients: record.recipients,
                from_name: record.from_name,
              });
              setResendModalVisible(true);
            }}
            title="재발송"
          />
          <Popconfirm
            title={`"${record.subject}" 이메일 로그를 삭제하시겠습니까?`}
            description="이 작업은 되돌릴 수 없습니다."
            onConfirm={() => handleDelete(record.email_log_id)}
          >
            <Button type="text" danger size="small" icon={<DeleteOutlined />} title="삭제" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    fetchEmailLogs();
    fetchStats();
  }, []);

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={4} style={{ margin: 0 }}>
            <MailOutlined /> 이메일 발송 내역 관리
          </Title>
          <Button 
            type="primary" 
            icon={<SendOutlined />}
            onClick={() => navigate('/admin/email')}
          >
            새메일발송
          </Button>
        </div>

        {/* 통계 */}
        {stats && (
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={4}>
              <Statistic title="전체" value={stats.total} />
            </Col>
            <Col span={4}>
              <Statistic title="발송됨" value={stats.sent} valueStyle={{ color: '#3f8600' }} />
            </Col>
            <Col span={4}>
              <Statistic title="실패" value={stats.failed} valueStyle={{ color: '#cf1322' }} />
            </Col>
            <Col span={4}>
              <Statistic title="대기중" value={stats.pending} valueStyle={{ color: '#faad14' }} />
            </Col>
            <Col span={4}>
              <Statistic title="취소됨" value={stats.cancelled} valueStyle={{ color: '#8c8c8c' }} />
            </Col>
          </Row>
        )}

        {/* 검색 폼 */}
        <Card size="small" style={{ marginBottom: 16 }}>
          <Form
            form={searchForm}
            layout="inline"
            onFinish={handleSearch}
          >
            <Form.Item name="query">
              <Input
                placeholder="제목, 내용, 수신자 검색"
                prefix={<SearchOutlined />}
                style={{ width: 200 }}
              />
            </Form.Item>
            <Form.Item name="status">
              <Select placeholder="상태" style={{ width: 120 }} allowClear>
                <Option value="SENT">발송됨</Option>
                <Option value="FAILED">실패</Option>
                <Option value="PENDING">대기중</Option>
                <Option value="CANCELLED">취소됨</Option>
              </Select>
            </Form.Item>
            <Form.Item name="email_type">
              <Select placeholder="타입" style={{ width: 120 }} allowClear>
                <Option value="BULK_EMAIL">대량 발송</Option>
                <Option value="BUG_REPORT_RESOLUTION">버그 리포트</Option>
                <Option value="SYSTEM_NOTIFICATION">시스템 알림</Option>
              </Select>
            </Form.Item>
            <Form.Item name="is_resend">
              <Select placeholder="재발송 여부" style={{ width: 120 }} allowClear>
                <Option value="true">재발송된 메일</Option>
                <Option value="false">원본 메일</Option>
              </Select>
            </Form.Item>
            <Form.Item name="date_range">
              <RangePicker placeholder={['시작일', '종료일']} />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                검색
              </Button>
            </Form.Item>
          </Form>
        </Card>

        {/* 테이블 */}
        <Table
          columns={columns}
          dataSource={emailLogs}
          rowKey="email_log_id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / 총 ${total}개`,
          }}
          onChange={handleTableChange}
          scroll={{ x: 760 }}
        />
      </Card>

      {/* 상세보기 모달 */}
      <Modal
        title="이메일 로그 상세"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedEmailLog && (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>제목:</Text>
                <br />
                <Text>{selectedEmailLog.subject}</Text>
              </Col>
              <Col span={12}>
                <Text strong>타입:</Text>
                <br />
                <Tag color={getTypeColor(selectedEmailLog.email_type)}>
                  {selectedEmailLog.email_type}
                </Tag>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Text strong>상태:</Text>
                <br />
                <Tag color={getStatusColor(selectedEmailLog.status)}>
                  {selectedEmailLog.status}
                </Tag>
              </Col>
              <Col span={12}>
                <Text strong>발송자:</Text>
                <br />
                <Text>{selectedEmailLog.sent_by?.username || '-'}</Text>
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Text strong>수신자 ({selectedEmailLog.recipients.length}명):</Text>
                <br />
                <Text>{selectedEmailLog.recipients.join(', ')}</Text>
              </Col>
              <Col span={12}>
                <Text strong>성공 ({selectedEmailLog.successful_recipients?.length || 0}명):</Text>
                <br />
                <Text>
                  {selectedEmailLog.successful_recipients && selectedEmailLog.successful_recipients.length > 0 
                    ? selectedEmailLog.successful_recipients.join(', ')
                    : selectedEmailLog.status === 'SENT' 
                      ? '발송 완료 (상세 정보 없음)'
                      : '발송되지 않음'
                  }
                </Text>
              </Col>
            </Row>
            {selectedEmailLog.failed_recipients && selectedEmailLog.failed_recipients.length > 0 && (
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Text strong>실패 ({selectedEmailLog.failed_recipients.length}명):</Text>
                  <br />
                  <Text type="danger">{selectedEmailLog.failed_recipients.join(', ')}</Text>
                </Col>
              </Row>
            )}
            {selectedEmailLog.error_message && (
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Text strong>오류 메시지:</Text>
                  <br />
                  <Text type="danger">{selectedEmailLog.error_message}</Text>
                </Col>
              </Row>
            )}
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={24}>
                <Text strong>내용:</Text>
                <br />
                <div
                  style={{
                    maxHeight: 200,
                    overflow: 'auto',
                    border: '1px solid #d9d9d9',
                    padding: 8,
                    borderRadius: 4,
                    backgroundColor: '#fafafa',
                  }}
                  dangerouslySetInnerHTML={{ __html: selectedEmailLog.content }}
                />
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Text strong>생성일:</Text>
                <br />
                <Text>{formatSimpleDateTime(selectedEmailLog.created_at)}</Text>
              </Col>
              <Col span={12}>
                <Text strong>발송일:</Text>
                <br />
                <Text>{selectedEmailLog.sent_at ? formatSimpleDateTime(selectedEmailLog.sent_at) : '-'}</Text>
              </Col>
            </Row>
          </div>
        )}
      </Modal>

      {/* 재발송 모달 */}
      <Modal
        title="이메일 재발송"
        open={resendModalVisible}
        onCancel={() => setResendModalVisible(false)}
        onOk={() => resendForm.submit()}
        width={600}
      >
        <Form
          form={resendForm}
          layout="vertical"
          onFinish={handleResend}
        >
          <Form.Item
            name="subject"
            label="제목"
            rules={[{ required: true, message: '제목을 입력하세요' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="content"
            label="내용"
            rules={[{ required: true, message: '내용을 입력하세요' }]}
          >
            <TextArea rows={6} />
          </Form.Item>
          <Form.Item
            name="recipients"
            label="수신자"
            rules={[{ required: true, message: '수신자를 입력하세요' }]}
          >
            <Select mode="tags" placeholder="이메일 주소를 입력하세요" />
          </Form.Item>
          <Form.Item name="from_name" label="발신자 이름">
            <Input />
          </Form.Item>
          <Form.Item>
            <Checkbox
              checked={hideOriginalAfterResend}
              onChange={(e) => setHideOriginalAfterResend(e.target.checked)}
            >
              재발송 후 원본 메일 숨기기
            </Checkbox>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default EmailLogManagement; 